import React, { useState, useEffect } from 'react';
import FormModal from '../../../components/shared/FormModal';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import { CREATE_CALENDAR_EVENT_URL, UPDATE_CALENDAR_EVENT_URL } from '../../../api/calendar';

function countWords(str) {
  if (!str || typeof str !== 'string') return 0;
  return str.trim().split(/\s+/).filter(Boolean).length;
}

function CreateEventModal({ isOpen, onClose, onSuccess, initialDate, eventToEdit }) {
  const isEditing = !!eventToEdit;
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [timeHour, setTimeHour] = useState('09');
  const [timeMinute, setTimeMinute] = useState('00');
  const [timeAmPm, setTimeAmPm] = useState('AM');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (eventToEdit) {
      setEventName(eventToEdit.event_name || '');
      setEventDate(eventToEdit.event_date || initialDate || '');
      setDescription(eventToEdit.description || '');

      if (eventToEdit.event_time) {
        const parts = eventToEdit.event_time.trim().split(' ');
        const timeParts = (parts[0] || '09:00').split(':');
        setTimeHour(timeParts[0] || '09');
        setTimeMinute(timeParts[1] || '00');
        setTimeAmPm((parts[1] || 'AM').toUpperCase());
      }
    } else {
      setEventName('');
      setEventDate(initialDate || new Date().toISOString().split('T')[0]);
      setDescription('');
      setTimeHour('09');
      setTimeMinute('00');
      setTimeAmPm('AM');
    }
  }, [eventToEdit, initialDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eventDate) {
      toast.warning('Date is required.');
      return;
    }

    if (!eventName.trim()) {
      toast.warning('Event Name is required.');
      return;
    }

    if (description && countWords(description) > 350) {
      toast.warning(`Description cannot exceed 350 words. Currently: ${countWords(description)} words.`);
      return;
    }

    const formattedTime = `${timeHour}:${timeMinute} ${timeAmPm}`;

    try {
      setIsSubmitting(true);
      
      const payload = {
        event_name: eventName.trim(),
        event_date: eventDate,
        event_time: formattedTime,
        description: description.trim()
      };

      if (isEditing) {
        const targetId = eventToEdit.original_id || eventToEdit.id;
        const response = await apiClient(UPDATE_CALENDAR_EVENT_URL(targetId), {
          method: 'PUT',
          body: payload
        });
        if (response) {
          toast.success('Event updated successfully.');
          if (onSuccess) onSuccess(response.event);
          onClose();
        }
      } else {
        const response = await apiClient(CREATE_CALENDAR_EVENT_URL, {
          method: 'POST',
          body: payload
        });

        if (response) {
          toast.success('Event created successfully.');
          if (onSuccess) onSuccess(response.event);
          onClose();
        }
      }
    } catch (err) {
      console.error('Error saving event:', err);
      toast.warning(err?.message || `Failed to ${isEditing ? 'update' : 'create'} event.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Event' : 'Create Event'}
      className="modal-medium"
    >
      <form onSubmit={handleSubmit} noValidate className="calendar-modal-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Date Field */}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            Date <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Time Field (12-Hour Format AM/PM) */}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            Time <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              className="form-control"
              value={timeHour}
              onChange={(e) => setTimeHour(e.target.value)}
              style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', flex: 1 }}
            >
              {hours.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <span style={{ fontWeight: 'bold' }}>:</span>
            <select
              className="form-control"
              value={timeMinute}
              onChange={(e) => setTimeMinute(e.target.value)}
              style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', flex: 1 }}
            >
              {minutes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              className="form-control"
              value={timeAmPm}
              onChange={(e) => setTimeAmPm(e.target.value)}
              style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', width: '80px', fontWeight: '600' }}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>

        {/* Event Name */}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            Event Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter event name..."
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Description */}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            Description <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'normal' }}>(Optional)</span>
          </label>
          <textarea
            className="form-control"
            placeholder="Enter description..."
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Modal Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
          <button
            type="button"
            className="action-btn-outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="action-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Event' : 'Save Event'}
          </button>
        </div>
      </form>
    </FormModal>
  );
}

export default CreateEventModal;
